import { apiClient } from "@/lib/api-client";

export type ObservabilityScope = "all" | "identity";
export type ObservabilityService = "identity-api" | "cognito-post-auth" | "cognito-pre-token";

export interface RecentFailureEvidence {
  logGroup: string;
  logPointer: string | null;
  traceId: string | null;
}

export interface RecentFailure {
  fingerprint: string;
  kind: "backend";
  severity: "error";
  service: string;
  environment: string;
  title: string;
  message: string;
  firstSeen: string;
  lastSeen: string;
  eventCount: number;
  evidence: RecentFailureEvidence;
}

export interface RecentFailuresPayload {
  environment: string;
  browser_capture_configured: boolean;
  queried_at: string;
  window: {
    from: string;
    to: string;
    minutes: number;
  };
  items: RecentFailure[];
}

export interface IncidentAlert {
  id: string;
  severity: "sev1" | "sev2" | "sev3";
  state: "open";
  environment: string;
  service: string;
  domain: "backend" | "browser" | "infrastructure";
  title: string;
  detectedAt: string;
  owner: string;
  eventCount: number;
}

export interface IncidentEvidenceSource {
  kind: "logs" | "trace" | "browser" | "infra";
  label: string;
  summary: string;
  locator: string;
}

export interface IncidentChangeContext {
  deployId: string | null;
  pullRequest: string | null;
  commitSha: string | null;
  author: string;
  startedAt: string;
  summary: string;
}

export interface IncidentSupportingSignal {
  kind: "backend" | "browser" | "infra";
  state: "regressed" | "open" | "stable";
  label: string;
  value: string;
  summary: string;
}

export interface IncidentTimelineEntry {
  at: string;
  kind: "deploy" | "alert" | "backend" | "browser" | "infra";
  title: string;
  summary: string;
}

export interface ActiveIncident {
  id: string;
  fingerprint: string;
  severity: "sev1" | "sev2" | "sev3";
  state: "open";
  environment: string;
  service: string;
  title: string;
  summary: string;
  owner: string;
  runbook: string;
  detectedAt: string;
  firstSeen: string;
  lastSeen: string;
  affected: string;
  eventCount: number;
  metrics: {
    detectMinutes: number;
    openMinutes: number;
    errorRatePct: number;
    p95LatencyMs: number;
  };
  evidenceSources: IncidentEvidenceSource[];
  changeContext: IncidentChangeContext;
  supportingSignals: IncidentSupportingSignal[];
  timeline: IncidentTimelineEntry[];
}

export interface IncidentSurfacePayload {
  environment: string;
  browser_capture_configured: boolean;
  queried_at: string;
  window: {
    from: string;
    to: string;
    minutes: number;
  };
  alerts: IncidentAlert[];
  activeIncident: ActiveIncident | null;
}

interface RecentFailuresResponse {
  data: RecentFailuresPayload;
}

interface IncidentSurfaceResponse {
  data: IncidentSurfacePayload;
}

export async function fetchRecentFailures(
  minutes: number,
  service?: string,
  scope: ObservabilityScope = "all"
): Promise<RecentFailuresPayload> {
  const params = new URLSearchParams({ minutes: String(minutes) });
  if (scope !== "all") params.set("scope", scope);
  if (service) params.set("service", service);
  const response = await apiClient.get<RecentFailuresResponse>(
    `/assistant/observability/recent-failures?${params.toString()}`
  );
  return response.data;
}

export async function fetchIncidentSurface(
  minutes: number,
  service?: string,
  scope: ObservabilityScope = "all"
): Promise<IncidentSurfacePayload> {
  const params = new URLSearchParams({ minutes: String(minutes) });
  if (scope !== "all") params.set("scope", scope);
  if (service) params.set("service", service);
  const response = await apiClient.get<IncidentSurfaceResponse>(
    `/assistant/observability/incident-surface?${params.toString()}`
  );
  return response.data;
}
